import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export type PdfViewerSrc = string | Uint8Array | ArrayBuffer | Blob;

@Component({
  selector: 'app-modal-invoice-pdf-viewer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './modal-invoice-pdf-viewer.component.html',
  styleUrls: ['./modal-invoice-pdf-viewer.component.css'],
})
export class ModalInvoicePdfViewerComponent implements OnChanges, OnDestroy {
  @Input() pdfSrc: PdfViewerSrc | null = null;
  @Input() invoiceNo = '';
  @Input() modalTitle = 'ใบกำกับภาษีแบบเต็มรูป (PDF Preview)';
  @Input() downloadFileName = '';
  @Output() close = new EventEmitter<void>();

  pdfUrl: SafeResourceUrl | null = null;
  private objectUrl: string | null = null;
  private ownedObjectUrl = false;

  constructor(private readonly sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pdfSrc']) {
      this.rebuildPreviewUrl();
    }
  }

  downloadPdf(): void {
    if (!this.pdfSrc) return;

    let href: string;
    let shouldRevoke = false;

    if (typeof this.pdfSrc === 'string') {
      href = this.pdfSrc;
    } else {
      const blob =
        this.pdfSrc instanceof Blob
          ? this.pdfSrc
          : new Blob([this.pdfSrc as BlobPart], { type: 'application/pdf' });
      href = URL.createObjectURL(blob);
      shouldRevoke = true;
    }

    const link = document.createElement('a');
    link.href = href;
    link.download = this.downloadFileName || `Invoice-${this.invoiceNo || 'Detail'}.pdf`;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (shouldRevoke) {
      setTimeout(() => URL.revokeObjectURL(href), 1500);
    }
  }

  onClose(): void {
    this.close.emit();
  }

  ngOnDestroy(): void {
    this.revokeOwnedObjectUrl();
  }

  private rebuildPreviewUrl(): void {
    this.revokeOwnedObjectUrl();
    this.pdfUrl = null;

    if (!this.pdfSrc) return;

    if (typeof this.pdfSrc === 'string') {
      this.objectUrl = this.pdfSrc;
      // Parent often hands a blob: URL — revoke on destroy to avoid leaks.
      this.ownedObjectUrl = this.pdfSrc.startsWith('blob:');
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfSrc);
      return;
    }

    const blob =
      this.pdfSrc instanceof Blob
        ? this.pdfSrc.type
          ? this.pdfSrc
          : new Blob([this.pdfSrc], { type: 'application/pdf' })
        : new Blob([this.pdfSrc as BlobPart], { type: 'application/pdf' });

    this.objectUrl = URL.createObjectURL(blob);
    this.ownedObjectUrl = true;
    this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
  }

  private revokeOwnedObjectUrl(): void {
    if (this.ownedObjectUrl && this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
    this.objectUrl = null;
    this.ownedObjectUrl = false;
  }
}
